import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';

export enum ConversationEnvironment {
  PRODUCTION = 'PRODUCTION',
  STAGING = 'STAGING',
}

registerEnum({ enum: ConversationEnvironment, name: 'ConversationEnvironment' });

/**
 * @entity MailAttachmentHeaderEntity
 */
@Entity()
export class MailAttachmentHeaderEntity extends AdornisEntity {
  static override _class = 'MailAttachmentHeaderEntity';

  @Field(() => ID) _id!: string;
  @Field(() => String) name: Maybe<string>;
  @Field(() => String) value: Maybe<string>;
}

/**
 * @entity MailAttachmentEntity
 */
@Entity()
export class MailAttachmentEntity extends AdornisEntity {
  static override _class = 'MailAttachmentEntity';

  @Field(() => ID) _id!: string;
  @Field(() => String) content: Maybe<string>;
  @Field(() => String) contentID: Maybe<string>;
  @Field(() => String) contentType: Maybe<string>;
  @Field(() => String) fileName: Maybe<string>;
  @Field(() => Boolean) isInline: Maybe<boolean>;
  @Field(() => [MailAttachmentHeaderEntity]) headers: Maybe<MailAttachmentHeaderEntity[]>;
}

@Entity()
export class MailEntity extends MongoEntity {
  static override _class = 'MailEntity';
  static override _collectionName = 'Mail';
  @Field(() => String) mailMessageId: Maybe<string>;
  @Field(() => String) odooTicketID: Maybe<string>;
  @Field(() => String) subject: Maybe<string>;
  @Field(() => String) body: Maybe<string>;
  @Field(() => String) from: Maybe<string>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => [MailAttachmentEntity]) attachments: Maybe<MailAttachmentEntity[]>;
  @Field(() => [String]) odooTags: Maybe<string[]>;
  @Field(() => String) team: Maybe<string>;
  @Field(() => String) answer: Maybe<string>;
  @Field(() => Float) priority: Maybe<number>;
  /** if true we do not consider this mail */
  @Field(() => Boolean) isFiltered: Maybe<boolean>;
  @Field(() => String) irrelevantCategory: Maybe<string>;
  // @Field(() => [String]) conversationHistory: Maybe<string[]>;
  // @Field(() => String) context: Maybe<string>;
  // @Field(() => [String]) odooModels: Maybe<string[]>;
  // @Field(() => ConversationEnvironment) environment: Maybe<ConversationEnvironment>;
  // @Field(() => Boolean) isPromptInjection: Maybe<boolean>;

  // // New fields for introspection in detail view
  // @Field(() => Float) answerScore: Maybe<number>;
  // @Field(() => String) answerComment: Maybe<string>;
  // @Field(() => String) guardrailPlainResponse: Maybe<string>;
  @Field(() => [String]) comments: Maybe<string[]>; // Array to store user comments
  @Field(() => Boolean) checkedAsIrrelevant: Maybe<boolean>;
  @Field(() => Boolean) suggestedAsIrrelevant: Maybe<boolean>;
  @Field(() => Boolean) isCheckedBySupport: Maybe<boolean>;

  static override async beforeCreate(entity: MailEntity) {
    await super.beforeCreate(entity);
    entity.createdAt = new Date();
    entity.isFiltered = false;
  }
}

// ensure indexes on startup
void (async () => {
  if (!A.isServer) return;
  const collection = await getRawCollection<MailEntity>(MailEntity._collectionName);
  await collection.createIndex({ createdAt: 1 });
})();

export async function getMailByPubsubMessageId(mailMessageIdCheck: string): Promise<MailEntity | null> {
  const collection = await getCollection<MailEntity>(MailEntity._class);
  const mail = await collection.findOne({ mailMessageId: mailMessageIdCheck });
  if (!mail) {
    logger.warn(`No MailEntity found for mailMessageId: ${mailMessageIdCheck}`);
    return null;
  }
  return new MailEntity(mail);
}

export async function setEmailAnswer(mailId: string, answer: string): Promise<void> {
  const collection = await getCollection<MailEntity>(MailEntity._class);
  await collection.updateOne({ _id: mailId }, { $set: { answer } });
}

// ? do we want to expose this somehow?
// TODO decide
export async function getAllIrrelevantSenders(): Promise<string[]> {
  const collection = await getCollection<MailEntity>(MailEntity._class);
  const emails = await collection
    .find({
      checkedAsIrrelevant: true,
      from: { $ne: null },
    })
    .toArray();
  const senders = emails.map(email => email.from).filter(Boolean) as string[];
  return Array.from(new Set(senders));
}
